<template>
  <v-row>
    <!-- ── Top filters ─────────────────────────────────────────────── -->
    <v-col cols="12">
      <v-card>
        <v-row class="pt-2 mx-2">
          <v-col cols="12" sm="6" class="px-4 pt-2 mt-1 mb-0 pb-0">
            <v-select
              v-model="selectedStatus"
              ref="selectedStatus"
              outlined dense
              :items="statusOptions"
              label="Filter by Status"
            />
          </v-col>

          <v-col cols="5" xs="5" class="text-left mt-0 pt-0">
            <v-text-field
              v-model="selectedStartDate"
              dense type="date"
              label="Start Date"
            />
          </v-col>

          <v-col cols="5" xs="5" class="text-right mt-0 pt-0">
            <v-text-field
              v-model="selectedEndDate"
              dense type="date"
              label="End Date"
            />
          </v-col>

          <v-col cols="2" xs="2" class="text-right mt-0 pt-0">
            <v-progress-circular
              v-if="loading"
              indeterminate color="primary" size="28"
            />
            <v-btn
              v-else
              icon small class="primary"
              @click="loadApplications"
            >
              <font-awesome-icon icon="fa-solid fa-search" />
            </v-btn>
          </v-col>
        </v-row>

        <v-card-title>
          <span class="me-3">
            ({{ filteredApplications.length }}) Reseller Applications
          </span>
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line hide-details
          />
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="filteredApplications"
          item-key="id"
          class="table-rounded"
          :items-per-page="10"
          disable-sort
          :search="search"
        >
          <!-- Applicant info -->
          <template #[`item.username`]="{ item }">
            <div class="d-flex flex-column">
              <span class="d-block font-weight-semibold text--primary text-truncate">
                {{ item.username }}
              </span>
              <small>{{ item.email }}</small>
            </div>
          </template>

          <!-- Business info -->
          <template #[`item.business_name`]="{ item }">
            <div class="d-flex flex-column">
              <span class="font-weight-medium">{{ item.business_name }}</span>
              <small class="grey--text">{{ item.business_type || '—' }}</small>
            </div>
          </template>

          <!-- Status chip — click opens dialog -->
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="statusColor[item.status]"
              dark small
              @click.stop="openDialog(item)"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template #[`item.created_at`]="{ item }">
            {{ item.created_at }}
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- ── Review Dialog ───────────────────────────────────────────── -->
    <v-dialog width="500px" v-model="setDialog">
      <v-card>
        <v-card-title class="primary white--text">
          {{ dialogTitle }}
        </v-card-title>

        <v-card-text class="black--text pt-3">
          <span v-html="applicationDetails" />

          <v-divider class="my-3" />

          <v-switch
            label="Approved"
            color="success"
            inset
            v-model="approvedState"
            @click="onApprovedClick"
          />
          <v-switch
            label="Rejected"
            color="error"
            inset
            v-model="rejectedState"
            @click="onRejectedClick"
          />

          <v-textarea
            v-if="rejectedState"
            v-model="rejectionReason"
            outlined dense rows="2"
            label="Rejection Reason (optional)"
            class="mt-2"
          />

          <v-alert v-if="reviewError" type="error" dense class="mt-2">
            {{ reviewError }}
          </v-alert>

          <v-btn
            color="primary"
            class="mt-2"
            :disabled="reviewing"
            @click="submitReview"
          >
            <v-progress-circular
              v-if="reviewing"
              indeterminate size="20" color="white"
            />
            <span v-else>Change</span>
          </v-btn>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" text @click="setDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ManagerResellerApplications',

  data() {
    return {
      headers: [
        { text: 'Applicant',  value: 'username'      },
        { text: 'Business',   value: 'business_name' },
        { text: 'Phone',      value: 'phone_contact' },
        { text: 'Status',     value: 'status'        },
        { text: 'Submitted',  value: 'created_at'    },
      ],

      applications:         [],
      filteredApplications: [],
      search:               '',
      loading:              false,

      // Filters
      selectedStatus:    'All',
      selectedStartDate: null,
      selectedEndDate:   null,
      statusOptions:     ['All', 'PENDING', 'APPROVED', 'REJECTED'],

      statusColor: {
        PENDING:  'primary',
        APPROVED: 'success',
        REJECTED: 'error',
      },

      // Dialog
      setDialog:           false,
      selectedApplication: null,
      dialogTitle:         'Review Reseller Application',
      applicationDetails:  '',
      approvedState:       false,
      rejectedState:       false,
      rejectionReason:     '',
      reviewing:           false,
      reviewError:         null,
    }
  },

  mounted() {
    document.title = 'Manager: Reseller Applications'
    this.loadApplications()
  },

  methods: {
    async loadApplications() {
      this.loading = true
      try {
        const params = {}
        if (this.selectedStatus && this.selectedStatus !== 'All') {
          params.status = this.selectedStatus
        }
        const res = await axios.get(
          '/api/v1/market_place/manager/reseller/applications/', { params }
        )
        this.applications = res.data
        this.applyDateFilter()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    applyDateFilter() {
      const data = [...this.applications]
      this.filteredApplications = data
    },

    openDialog(item) {
      this.selectedApplication = item
      this.approvedState       = item.status === 'APPROVED'
      this.rejectedState       = item.status === 'REJECTED'
      this.rejectionReason     = item.rejection_reason || ''
      this.reviewError         = null
      this.applicationDetails  = (
        `Review the application of <b>'${item.username}'</b> ` +
        `for business <b>'${item.business_name}'</b>` +
        (item.business_type ? ` (${item.business_type})` : '') +
        (item.phone_contact  ? ` — ${item.phone_contact}` : '')
      )
      this.setDialog = true
    },

    onApprovedClick() {
      if (this.approvedState) this.rejectedState = false
    },

    onRejectedClick() {
      if (this.rejectedState) this.approvedState = false
    },

    async submitReview() {
      if (!this.approvedState && !this.rejectedState) {
        this.$store.commit('setSnackBarMessage', 'Error: Please select a state')
        this.$store.commit('setSnackBarColor',   'red darken-4')
        this.$store.commit('activateSnackBar',   true)
        this.$store.commit('setSnackBarTimeOut', 4000)
        return
      }

      this.reviewing   = true
      this.reviewError = null

      const action  = this.approvedState ? 'APPROVE' : 'REJECT'
      const payload = { action }
      if (action === 'REJECT' && this.rejectionReason) {
        payload.rejection_reason = this.rejectionReason
      }

      try {
        const res = await axios.post(
          `/api/v1/market_place/manager/reseller/applications/${this.selectedApplication.id}/review/`,
          payload
        )
        this.$store.commit('setSnackBarMessage', res.data.message)
        this.$store.commit('setSnackBarColor',   'primary darken-4')
        this.$store.commit('activateSnackBar',   true)
        this.$store.commit('setSnackBarTimeOut', 4000)
        this.setDialog = false
        await this.loadApplications()
      } catch (err) {
        this.reviewError = err.response?.data?.error || 'Action failed.'
      } finally {
        this.reviewing = false
      }
    },

    handleError(err) {
      const msg = err.response
        ? `${err.response.status} ${err.response.statusText}`
        : 'An error occurred. Please check your internet connection and try again later.'
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor',   'red darken-4')
      this.$store.commit('activateSnackBar',   true)
      this.$store.commit('setSnackBarTimeOut', 4000)
    },
  },
}
</script>
